import axios, { AxiosError, AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'
import { ProjectMetadata } from '../../../data/model/results'
import requestWithRetry from '../../../utils/requestWithRetry'
import {
  PublicResultsApiRouting,
  buildProjectMetadataUrl,
  buildPublicApiRouting,
  getPublicRestDomainLegacy,
  getPublicRestDomainV2
} from '../publicApiSource'

interface Data {
  loading: boolean
  error: SurveyMetadataError
  data: ProjectMetadata | undefined
  apiRouting: PublicResultsApiRouting | undefined
}

interface Vars {
  surveyId: string
}

interface SurveyMetadataError {
  isError: boolean
  reason?: '404' | 'other'
}

const fetchProjectMetadata = async (
  baseUrl: string,
  surveyId: string
): Promise<AxiosResponse<ProjectMetadata>> => {
  return requestWithRetry({
    retryCount: 0,
    method: 'get',
    url: buildProjectMetadataUrl(baseUrl, surveyId)
  })
}

export const useSurveyMetadata = ({ surveyId }: Vars): Data => {
  const [error, setError] = useState<SurveyMetadataError>({ isError: false })
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<ProjectMetadata>()
  const [apiRouting, setApiRouting] = useState<
    PublicResultsApiRouting | undefined
  >()

  useEffect(() => {
    let isCancelled = false

    const resolveMetadata = async (): Promise<void> => {
      setLoading(true)
      setApiRouting(undefined)

      const v2BaseUrl = getPublicRestDomainV2()
      if (v2BaseUrl != null) {
        try {
          const v2Response = await fetchProjectMetadata(v2BaseUrl, surveyId)
          if (isCancelled) {
            return
          }
          if (v2Response.status >= 200 && v2Response.status < 400) {
            setError({ isError: false })
            setData(v2Response.data)
            setApiRouting(buildPublicApiRouting('v2'))
            return
          }
        } catch (requestError) {
          if (isCancelled || axios.isCancel(requestError)) {
            return
          }
          // Any V2 failure (404, 5xx, network) falls through to legacy so a
          // degraded or misconfigured V2 does not block public viewing.
        }
      }

      const legacyBaseUrl = getPublicRestDomainLegacy()
      if (legacyBaseUrl === '') {
        if (!isCancelled) {
          setError({ isError: true, reason: 'other' })
        }
        return
      }

      try {
        const legacyResponse = await fetchProjectMetadata(
          legacyBaseUrl,
          surveyId
        )
        if (isCancelled) {
          return
        }
        if (legacyResponse.status >= 200 && legacyResponse.status < 400) {
          setError({ isError: false })
          setData(legacyResponse.data)
          setApiRouting(buildPublicApiRouting('legacy'))
          return
        }
        setError({ isError: true, reason: 'other' })
      } catch (requestError) {
        if (isCancelled || axios.isCancel(requestError)) {
          return
        }
        const reason =
          (requestError as AxiosError).response?.status === 404
            ? '404'
            : 'other'
        setError({ isError: true, reason })
      }
    }

    resolveMetadata().finally(() => {
      if (!isCancelled) {
        setLoading(false)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [surveyId])

  return { data, error, loading, apiRouting }
}
